import type {RefObject} from 'react';

import styled from '@emotion/styled';
import {observer} from 'mobx-react-lite';

import {usePayloadImage} from './usePayloadImage';
import {transient} from 'Application/configs/emotionTransientProps';

import type {ImageUpload} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the shape of the props expected by the `PayloadImage` component.
 * It provides optional styling and configuration props like `className` and `expectedSizes` to control layout and responsiveness.
 * The `image` prop accepts either a media object, string reference, or null, supporting flexible CMS-driven content.
 * An optional `testId` helps facilitate test automation by providing a consistent `data-cy` attribute.
 */
interface ComponentProps {
    /**
     * Optional class name applied to the rendered `<img>` element for styling purposes.
     */
    className?: string;
    /**
     * An optional object specifying responsive size breakpoints for image rendering.
     * These values influence the `sizes` attribute and optimize image delivery for different screen widths.
     */
    expectedSizes?: {
        lg?: number;
        md?: number;
        sm?: number;
        xl?: number;
        xs?: number;
        xxl?: number;
    };
    /**
     * Indicates the fetch priority of the image resource.
     */
    fetchpriority?: 'auto' | 'high' | 'low';
    /**
     * The image source, which may be a fully populated `Media` object, a string ID, or `null`/`undefined`.
     * Used by the `usePayloadImage` hook to resolve responsive image data such as `srcSet`, `alt`, and `src`.
     */
    image: ImageUpload | null | undefined;
    /**
     * Whether to enable lazy loading for the image. Defaults to `true` for performance optimization.
     * When `true`, the image will load only when it enters the viewport.
     */
    isLazy?: boolean;
    /**
     * If `true`, the image is considered purely decorative and will have `aria-hidden` set to `true`.
     * This indicates to assistive technologies that the image can be ignored.
     *
     * @default false
     */
    isPresentational?: boolean;
    ref?: RefObject<HTMLImageElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'PayloadImage'
     */
    testId?: string;
}

/**
 * A React component that renders a responsive `<picture>` element for optimized image loading with Payload CMS support.
 * This component leverages the `usePayloadImage` hook to extract the necessary image metadata including `src`, `srcSet`,
 * fallback sources, and `alt` text. It supports modern image formats by prioritizing WebP and falling back to PNG,
 * and uses lazy loading with async decoding for performance. An optional `testId` is included to facilitate end-to-end testing.
 *
 * @param props                  The component props.
 * @param props.className        Optional class name to apply to the underlying `<img>` element.
 * @param props.expectedSizes    A string indicating the `sizes` attribute for responsive image behavior.
 * @param props.fetchpriority    The fetch priority of the image resource.
 * @param props.image            The image object from Payload CMS used to generate the responsive sources.
 * @param props.isLazy           Whether to enable lazy loading for the image (default: `true`).
 * @param props.testId           Optional test ID for use with testing tools like Cypress (default: `'PayloadImage'`).
 * @param props.isPresentational If `true`, marks the image as decorative with `aria-hidden` (default: `false`).
 * @returns A JSX element rendering a fully responsive image using the `<picture>` element.
 *
 * @example
 * ```tsx
 * <PayloadImage
 *   image={hero.image}
 *   expectedSizes="(max-width: 768px) 100vw, 50vw"
 *   className="rounded-md"
 * />
 * ```
 */
const PayloadImage = observer(({
    className,
    expectedSizes,
    fetchpriority,
    image,
    isLazy = true,
    isPresentational = false,
    ref,
    testId = 'PayloadImage'
}: ComponentProps) => {
    const {alt, height, sources, src, width} = usePayloadImage(image, expectedSizes);

    return (
        <Picture data-cy={testId}>
            {sources.map(source => (
                <source
                    key={source.srcSet}
                    media={source.media}
                    sizes={source.sizes}
                    srcSet={source.srcSet}
                    type={source.type}
                />
            ))}
            <Image
                ref={ref}
                $height={height}
                $width={width}
                alt={isPresentational ? '' : alt}
                className={className}
                data-cy={`${testId}-image`}
                decoding="async"
                fetchPriority={fetchpriority}
                loading={isLazy ? 'lazy' : 'eager'}
                role={isPresentational ? 'presentation' : undefined}
                src={src}
            />
        </Picture>
    );
});

PayloadImage.displayName = 'PayloadImage';

export {PayloadImage};

const Picture = styled.picture`
    display: block;
    max-height: 100%;
    max-width: 100%;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
`;

interface ImageProps {
    $height?: number | null;
    $width?: number | null;
}

const Image = styled('img', {...transient})<ImageProps>`
    aspect-ratio: ${({$height, $width}) => ($width && $height ? `${$width} / ${$height}` : 'auto')};
    display: block;
    height: auto;
    object-fit: contain;
    width: 100%;
`;